import { Box, Button, Chip, CircularProgress, Tooltip } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Bucket, BucketNumberEnum } from "features/trading/buckets";
import { MemberHistoryStatusEnum, historyCounts } from "./constants";
import { BucketActions } from "./useBucketActions";

interface BucketHistorySummaryProps {
  bucket: Bucket;
  actions: Pick<BucketActions, "ensureHistory" | "isEnsuringHistory">;
}

/**
 * Account-history health of a bucket's included members: how many pairs are
 * report-ready, how many failed to generate (with a retry button) and how
 * many were never attempted. Persistent — computed from the live member
 * statuses, not from the last generation run. Bucket 3 doesn't use
 * AccountHistory (its report values are calculated dynamically), so it
 * shows no summary.
 */
export const BucketHistorySummary = ({
  bucket,
  actions,
}: BucketHistorySummaryProps) => {
  if (bucket.number === BucketNumberEnum.BUCKET_3) return null;

  const counts = historyCounts(bucket);
  const ready = counts[MemberHistoryStatusEnum.READY];
  const failed = counts[MemberHistoryStatusEnum.FAILED];
  const pending = counts[MemberHistoryStatusEnum.PENDING];
  const missing = failed + pending;
  if (ready + missing === 0) return null;

  return (
    <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
      <Tooltip title="Assets with account history — reports can run for them">
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`${ready} with history`}
        />
      </Tooltip>
      {failed > 0 && (
        <Tooltip title="History generation failed — hover the asset's status icon for the reason">
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`${failed} failed`}
          />
        </Tooltip>
      )}
      {pending > 0 && (
        <Tooltip title="History was never generated for these assets yet">
          <Chip
            size="small"
            variant="outlined"
            label={`${pending} pending`}
          />
        </Tooltip>
      )}
      {missing > 0 && (
        <Button
          size="small"
          disabled={actions.isEnsuringHistory}
          startIcon={
            actions.isEnsuringHistory ? (
              <CircularProgress size={14} />
            ) : (
              <RefreshIcon fontSize="small" />
            )
          }
          onClick={() => actions.ensureHistory()}
        >
          Generate histories
        </Button>
      )}
    </Box>
  );
};
